import { IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { icon_browser } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import AsyncFetchSelect from 'components/common/select/AsyncFetchSelect';
import T from 'components/common/text/T';
import ProjectFileGroup from 'components/project/ProjectFileGroup';
import ProjectFileGroupLocal from 'components/project/ProjectFileGroupLocal';
import { ProjectContext } from 'contexts/project/ProjectContext';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface ProjectFileListProps {
  projectInfo: any;
  isSelectedPartner: boolean;
  hasAlarm: any;
  hasViewer: boolean;

  localFileInput: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
    reset: () => void;
  };
  localFileInputRef: React.MutableRefObject<any>;
  localFileListCt: any;
  partnerFileList: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  cloudFileList: {
    value: any;
    onChange: (e: any) => void;
    setValue: React.Dispatch<any>;
  };
  syncPartners: {
    email: string;
    groupCode: string;
    isSelf: number;
    name: string;
    userTypeIdx: number | null;
    userGroupIdx: number;
  }[];
  hasMoreSyncPartners: boolean;
  selectedSyncPartner: {
    value: string;
    onChange: (e: any) => void;
    setValue: React.Dispatch<string>;
  };
  isModelFileLoadingProgress: {
    value: boolean;
    onChange: (e: boolean) => void;
    setValue: React.Dispatch<string>;
  };
  selectedModelFile: any;
  onAddLocalFiles: (data: any) => void;
  onClearView: () => void;
  onFetchSyncPartners: () => void;
  onSearchSyncPartners: (value: string) => void;
  onClickFileImport: () => void;
  onClickImageFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickImageFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickViewModelFile: (e: any, id: any, data?: any) => Promise<any>;
  onClickViewModelFileLocal: (e: any, id: any, data?: any) => Promise<any>;
  onClickWireframe: (id: number, data: any) => void;
  onChangeModelOpacity: (e: any, id: number, newValue: number | number[]) => void;
  onClickCheckbox: (e: any, id: any, storage: string, data: any) => void;
  onClickCheckboxAll: (e: any, storage: string, isCheckAll: boolean, shareType: string) => void;
  onClickDeleteFiles: (storage: string, deletelist: any[]) => void;
  onClickDownloadFiles: (id: number, name: string) => Promise<any>;
  onClickWebViewer: () => void;
}

function ProjectFileList({
  projectInfo,
  isSelectedPartner,
  hasAlarm,
  hasViewer,
  localFileInput,
  localFileInputRef,
  localFileListCt,
  partnerFileList,
  cloudFileList,
  syncPartners,
  hasMoreSyncPartners,
  selectedSyncPartner,
  isModelFileLoadingProgress,
  selectedModelFile,
  onClearView,
  onAddLocalFiles,
  onClickFileImport,
  onFetchSyncPartners,
  onSearchSyncPartners,
  onClickImageFile,
  onClickImageFileLocal,
  onClickViewModelFile,
  onClickViewModelFileLocal,
  onClickWireframe,
  onChangeModelOpacity,
  onClickCheckbox,
  onClickCheckboxAll,
  onClickDeleteFiles,
  onClickDownloadFiles,
  onClickWebViewer,
}: ProjectFileListProps) {
  const { company, userGroupIdx, syncPartnersPending, isClinicUser } = useShallowAppSelector(
    state => ({
      company: state.user.user?.company,
      userGroupIdx: state.user.user?.userGroupIdx,
      syncPartnersPending: state.partner.syncPartners.pending,
      isClinicUser: isClinicUserSelector(state),
    }),
  );

  const { t } = useTranslation();
  const syncInfo = projectInfo?.syncInfo;
  const { onLoadLocalFiles, onClickOpenWorkspace } = useContext(ProjectContext);
  const { ipcRenderer } = window;

  return (
    <Styled.ProjectFileList data-component-name="ProjectFileList">
      {!!ipcRenderer && (
        <div className="projectFileList__extra_button_container">
          <div className="projectFileList__extra_button_box left">
            <MuiButton
              className="projectFileList__extra_button open"
              config={{
                borderColor: '#E1E1E7',
                bgColor: 'white',
              }}
              disabled={hasAlarm}
              disableElevation
              variant="outlined"
              onClick={() => onClickFileImport()}
            >
              <span>
                <T>FILE_IMPORT</T>
              </span>
            </MuiButton>
            <MuiButton
              className="projectFileList__extra_button workspace"
              config={{
                borderColor: '#E1E1E7',
                bgColor: 'white',
              }}
              disableElevation
              disabled={hasAlarm}
              variant="outlined"
              onClick={() => {
                onClickOpenWorkspace(projectInfo.projectId);
              }}
            >
              <T>PROJECT_OPEN_WORKSPACE</T>
            </MuiButton>

            <MuiButton
              className="projectFileList__extra_button refresh"
              config={{
                borderColor: '#E1E1E7',
                bgColor: 'white',
              }}
              disabled={hasAlarm}
              disableElevation
              variant="outlined"
              onClick={() => {
                onClearView();
                onLoadLocalFiles();
              }}
            >
              <RefreshIcon
                htmlColor={!hasAlarm ? color.stage_completed : color.gray_b5}
                fontSize="inherit"
              />
            </MuiButton>
          </div>
          <div className="projectFileList__extra_button_box right">
            <IconButton
              className="projectFileList__extra_button"
              onClick={() => {
                onClickWebViewer();
              }}
            >
              <img src={icon_browser} />
            </IconButton>
          </div>
        </div>
      )}
      <div className="projectFileList__container">
        <div className="projectUpload_group_wrapper">
          <ProjectFileGroup
            company={company}
            projectCode={projectInfo.projectCode}
            projectId={projectInfo.projectId}
            hasViewer={hasViewer}
            fileList={partnerFileList}
            title={t('GLOBAL_PARTNER')}
            shareType={'partner'}
            isModelFileLoadingProgress={isModelFileLoadingProgress}
            selectedModelFile={selectedModelFile}
            onClickCheckbox={onClickCheckbox}
            onClickCheckboxAll={onClickCheckboxAll}
            onClickWireframe={onClickWireframe}
            onChangeModelOpacity={onChangeModelOpacity}
            onClickImageFile={onClickImageFile}
            onClickViewModelFile={onClickViewModelFile}
            onClickDeleteFiles={onClickDeleteFiles}
            onClickDownloadFiles={onClickDownloadFiles}
          />

          <ProjectFileGroup
            company={company}
            projectCode={projectInfo.projectCode}
            projectId={projectInfo.projectId}
            hasViewer={hasViewer}
            fileList={cloudFileList}
            title={t('GLOBAL_GROUP')}
            shareType={'group'}
            isModelFileLoadingProgress={isModelFileLoadingProgress}
            selectedModelFile={selectedModelFile}
            onClickCheckbox={onClickCheckbox}
            onClickCheckboxAll={onClickCheckboxAll}
            onClickWireframe={onClickWireframe}
            onChangeModelOpacity={onChangeModelOpacity}
            onClickImageFile={onClickImageFile}
            onClickViewModelFile={onClickViewModelFile}
            onClickDeleteFiles={onClickDeleteFiles}
            onClickDownloadFiles={onClickDownloadFiles}
          />

          <ProjectFileGroupLocal
            projectId={projectInfo.projectId}
            hasAlarm={hasAlarm}
            hasViewer={hasViewer}
            fileList={localFileListCt}
            title={t('GLOBAL_LOCAL')}
            localFileInputRef={localFileInputRef}
            localFileInput={localFileInput}
            isModelFileLoadingProgress={isModelFileLoadingProgress}
            selectedModelFile={selectedModelFile}
            onAddLocalFiles={onAddLocalFiles}
            onClickFileImport={onClickFileImport}
            onClickWireframe={onClickWireframe}
            onChangeModelOpacity={onChangeModelOpacity}
            onClickViewModelFileLocal={onClickViewModelFileLocal}
            onClickImageFileLocal={onClickImageFileLocal}
            onClickCheckbox={onClickCheckbox}
            onClickCheckboxAll={onClickCheckboxAll}
            onClickDeleteFiles={onClickDeleteFiles}
          />
        </div>
        <div className="projectFileList__select_partner_box">
          {!projectInfo?.partnershipCode ? (
            <>
              {syncInfo?.syncStatus === 0 ? (
                <>
                  <div className="projectFileList__select_partner_label">
                    <T>REQUESTED_PARTNER</T>
                  </div>
                  <div className="projectFileList__select_partner_name">
                    <div className="projectFileList__select_partner fake_box">
                      <p className="text-overflow-ellipsis" title={syncInfo?.partnerName}>
                        {syncInfo?.partnerName}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="projectFileList__select_partner_label">
                    {!!isSelectedPartner ? <T>REQUESTED_PARTNER</T> : <T>SELECT_PARTNER</T>}
                  </div>

                  <div className="projectFileList__select_partner_name">
                    {!!projectInfo.clientGroupCode || isClinicUser ? (
                      <AsyncFetchSelect
                        disabled={isSelectedPartner}
                        className="sm"
                        fullWidth
                        fontColor="#303030"
                        bgColor="#ffffff"
                        borderColor="#e1e1e1"
                        hoverBorderColor="#7CBEF8"
                        loadingIconColor="#7CBEF8"
                        dropIconColor="#bbbbbb"
                        placeholderColor="#e1e1e1"
                        inputProps={{
                          placeholder: t('SELECT_PARTNER'),
                          selectedValue: selectedSyncPartner.value,
                          setSelectedValue: selectedSyncPartner.setValue,
                          data: syncPartners,
                          hasMoreData: hasMoreSyncPartners,
                          idKey: 'userGroupIdx',
                          labelKey: 'name',
                          exceptId: userGroupIdx,
                          onFetch: onFetchSyncPartners,
                          searchLoading: syncPartnersPending,
                          onSearch: onSearchSyncPartners,
                        }}
                      />
                    ) : (
                      <span></span>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="projectFileList__select_partner_label">
                <T>GLOBAL_PARTNER</T>
              </div>
              <div className="projectFileList__select_partner_name">
                <div className="projectFileList__select_partner fake_box">
                  <p className="text-overflow-ellipsis" title={projectInfo?.partnerName}>
                    {projectInfo?.partnerName}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Styled.ProjectFileList>
  );
}

export default React.memo(ProjectFileList);

const Styled = {
  ProjectFileList: styled.div`
    width: 100%;
    position: relative;

    .projectFileList__extra_button_container {
      position: absolute;
      top: -52px;
      left: 400px;
      width: 1237px;
      height: 52px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .projectFileList__extra_button_box {
      position: relative;
      z-index: 200;
      padding: 0 10px;
      &.left {
        display: flex;
        align-items: center;
        justify-content: space-between;

        column-gap: 10px;

        .projectFileList__extra_button {
          span {
            color: #444;
          }
          width: 130px;
          height: 38px;
          border-radius: 3px;
          font-size: 14px;
          padding: 0;
          &.refresh {
            width: 50px;
            font-size: 28px;
          }
        }
      }

      &.right {
        .projectFileList__extra_button {
          padding: 5px;
        }
      }
    }

    .projectFileList__select_partner_box {
      border-top: 1px solid #e1e1e7;

      /* margin-top: 23px; */
      padding: 23px 21px 0 21px;
      /* border-radius: 0 0 5px 5px; */
      display: flex;
      align-items: center;
      justify-content: space-between;

      .projectFileList__select_partner_label {
        font-size: 14px;
        font-weight: 500;
        color: #303030;
      }
      .projectFileList__select_partner_name {
        width: 267px;
        .projectFileList__select_partner {
          &.fake_box {
            width: 100%;
            height: 32px;
            border: 1px solid #e1e1e1;
            display: flex;
            align-items: center;
            padding: 0 8px;
            padding-right: 55px;
            font-size: 12px;
            border-radius: 5px;
          }
        }
      }
    }
  `,
};
